/**
 * Translations for the custom sticker configurator (StickerConfigurator.tsx).
 * Keys are prefixed with `cf.` and merged into the main dictionary in I18nContext.
 */
export type CfLang = "pl" | "en" | "de" | "it" | "fr";

export const configuratorTranslations: Record<CfLang, Record<string, string>> = {
  pl: {
    "cf.err.dims": "Podaj poprawne wymiary naklejki",

    "cf.toast.fromContour": 'Plik "{f}" dodany z kreatora konturów!',
    "cf.toast.fileAddedCart": 'Plik "{f}" dodany do koszyka!',
    "cf.toast.productAdded": "Produkt dodany do koszyka!",
    "cf.toast.linkCopied": "Link skopiowany do schowka!",
    "cf.toast.loginCart": "Zaloguj się, aby dodać do koszyka",
    "cf.toast.addedToCart": "Dodano do koszyka!",
    "cf.toast.uploadCancelled": "Wgrywanie anulowane",
    "cf.toast.compressed": "Skompresowano {n} obrazów — zaoszczędzono {mb} MB",

    "cf.foil.title": "Rodzaj folii",
    "cf.foil.sub": "jest to podkład na którym drukowane są naklejki",
    "cf.foil.loading": "Ładowanie folii...",
    "cf.foil.error": "Nie udało się załadować folii.",
    "cf.foil.none": "Brak dostępnych folii",
    "cf.lam.title": "Rodzaj laminatu",
    "cf.lam.sub": "jest to folia którą zostają pokryte naklejki",
    "cf.lam.loading": "Ładowanie laminatów...",
    "cf.lam.error": "Nie udało się załadować laminatów.",
    "cf.retry": "Spróbuj ponownie",

    "cf.drop.here": "Upuść pliki tutaj...",
    "cf.drop.hint": "Przeciągnij i upuść lub kliknij, aby wybrać",
    "cf.files.suffix": "plik(ów)",

    "cf.up.title": "Trwa przesyłanie plików...",
    "cf.up.wait": "Wgrywasz duży plik — prosimy o cierpliwość, to może chwilę potrwać.",
    "cf.up.remaining": "pozostało",
    "cf.up.slowTitle": "Wolne połączenie internetowe",
    "cf.up.slowDesc": "Wgrywanie trwa dłużej niż zwykle. Spróbuj przełączyć się na szybszą sieć Wi-Fi lub bliżej routera.",
    "cf.up.dontClose": "Nie zamykaj strony podczas przesyłania",
    "cf.up.cancel": "Anuluj wgrywanie",

    "cf.file.uploaded": "Plik wgrany na serwer",
    "cf.file.noThumb": "W przypadku niektórych formatów plików (np. .tif, .ai, .eps) miniatury mogą być niewidoczne, ale pliki zostały wgrane pomyślnie.",

    "cf.three.desc": "W jednym zamówieniu można podpiąć maksymalnie trzy projekty wykonane w tym samym rodzaju folii i laminatu. Wszystkie projekty muszą mieć zbliżone wymiary.",
    "cf.three.notApply": "✨ Ciebie to nie dotyczy — masz indywidualne warunki!",
    "cf.three.exampleTitle": "📌 Przykład:",
    "cf.three.example1": "Folia biała + laminat matowy, naklejki 10×10 cm, 120 sztuk — możesz dodać maksymalnie 3 różne projekty.",
    "cf.three.example2": "W takim przypadku ilość dzielimy po równo między projekty.",

    "cf.fmt.accepted": "Akceptowane formaty:",
    "cf.fmt.max": "maks.",
    "cf.fmt.perFile": "na plik",
    "cf.fmt.dpi": "Zalecana rozdzielczość min. 600 DPI (akceptujemy min. 300 DPI).",
    "cf.fmt.big1": "Pliki powyżej 20 MB prosimy wysyłać bezpośrednio na",
    "cf.fmt.big2": "lub przez",
    "cf.fmt.contact": "Jeżeli z plikami będzie jakiś problem — skontaktujemy się z Tobą przed drukiem.",

    "cf.net": "netto",
    "cf.btn.added": "Dodano do koszyka ✓",
    "cf.btn.uploading": "Trwa przesyłanie plików...",
    "cf.btn.share": "Udostępnij ustawienia",
    "cf.btn.shareHint": "Tworzy aktywny link z zapisanymi ustawieniami naklejek",
    "cf.btn.save": "Zapisz konfigurację",
    "cf.cfg.loginSave": "Zaloguj się, aby zapisać konfigurację",
    "cf.cfg.namePrompt": "Nadaj nazwę konfiguracji (opcjonalnie):",
    "cf.cfg.default": "Moja konfiguracja",
    "cf.cfg.saveError": "Błąd zapisu konfiguracji",
    "cf.cfg.saved": "Konfiguracja zapisana! Znajdziesz ją w koncie.",
  },

  en: {
    "cf.err.dims": "Enter valid sticker dimensions",

    "cf.toast.fromContour": 'File "{f}" added from the contour creator!',
    "cf.toast.fileAddedCart": 'File "{f}" added to cart!',
    "cf.toast.productAdded": "Product added to cart!",
    "cf.toast.linkCopied": "Link copied to clipboard!",
    "cf.toast.loginCart": "Please log in to add to cart",
    "cf.toast.addedToCart": "Added to cart!",
    "cf.toast.uploadCancelled": "Upload cancelled",
    "cf.toast.compressed": "Compressed {n} image(s) — saved {mb} MB",

    "cf.foil.title": "Sticker material",
    "cf.foil.sub": "the base material your stickers are printed on",
    "cf.foil.loading": "Loading materials...",
    "cf.foil.error": "Failed to load materials.",
    "cf.foil.none": "No materials available",
    "cf.lam.title": "Laminate type",
    "cf.lam.sub": "the protective film covering your stickers",
    "cf.lam.loading": "Loading laminates...",
    "cf.lam.error": "Failed to load laminates.",
    "cf.retry": "Try again",

    "cf.drop.here": "Drop files here...",
    "cf.drop.hint": "Drag & drop or click to browse",
    "cf.files.suffix": "file(s)",

    "cf.up.title": "Uploading files...",
    "cf.up.wait": "You're uploading a large file — please be patient, this may take a moment.",
    "cf.up.remaining": "remaining",
    "cf.up.slowTitle": "Slow internet connection",
    "cf.up.slowDesc": "Upload is slower than usual. Try switching to a faster Wi-Fi network or move closer to your router.",
    "cf.up.dontClose": "Do not close the page while uploading",
    "cf.up.cancel": "Cancel upload",

    "cf.file.uploaded": "File uploaded to server",
    "cf.file.noThumb": "For some file formats (e.g. .tif, .ai, .eps) thumbnails may not be visible, but the files have been uploaded successfully.",

    "cf.three.desc": "You can attach up to three designs per order, printed on the same material and laminate. All designs must have similar dimensions.",
    "cf.three.notApply": "✨ This doesn't apply to you — you have individual terms!",
    "cf.three.exampleTitle": "📌 Example:",
    "cf.three.example1": "White material + matte laminate, 10×10 cm stickers, 120 pcs — you can add up to 3 different designs.",
    "cf.three.example2": "In that case the quantity is divided equally among the designs.",

    "cf.fmt.accepted": "Accepted formats:",
    "cf.fmt.max": "max",
    "cf.fmt.perFile": "per file",
    "cf.fmt.dpi": "Recommended resolution min. 600 DPI (minimum accepted 300 DPI).",
    "cf.fmt.big1": "Files over 20 MB — please send them directly to",
    "cf.fmt.big2": "or via",
    "cf.fmt.contact": "If there's any issue with your files — we'll contact you before printing.",

    "cf.net": "net",
    "cf.btn.added": "Added to cart ✓",
    "cf.btn.uploading": "Uploading files...",
    "cf.btn.share": "Share settings",
    "cf.btn.shareHint": "Creates a live link with your saved sticker settings",
    "cf.btn.save": "Save configuration",
    "cf.cfg.loginSave": "Log in to save the configuration",
    "cf.cfg.namePrompt": "Name your configuration (optional):",
    "cf.cfg.default": "My configuration",
    "cf.cfg.saveError": "Error saving configuration",
    "cf.cfg.saved": "Configuration saved! You'll find it in your account.",
  },

  de: {
    "cf.err.dims": "Bitte gültige Aufkleber-Maße angeben",

    "cf.toast.fromContour": 'Datei "{f}" aus dem Konturen-Editor hinzugefügt!',
    "cf.toast.fileAddedCart": 'Datei "{f}" zum Warenkorb hinzugefügt!',
    "cf.toast.productAdded": "Produkt zum Warenkorb hinzugefügt!",
    "cf.toast.linkCopied": "Link in die Zwischenablage kopiert!",
    "cf.toast.loginCart": "Bitte melden Sie sich an, um in den Warenkorb zu legen",
    "cf.toast.addedToCart": "Zum Warenkorb hinzugefügt!",
    "cf.toast.uploadCancelled": "Upload abgebrochen",
    "cf.toast.compressed": "{n} Bild(er) komprimiert — {mb} MB gespart",

    "cf.foil.title": "Materialart",
    "cf.foil.sub": "der Träger, auf dem die Aufkleber gedruckt werden",
    "cf.foil.loading": "Materialien werden geladen...",
    "cf.foil.error": "Materialien konnten nicht geladen werden.",
    "cf.foil.none": "Keine Materialien verfügbar",
    "cf.lam.title": "Laminatart",
    "cf.lam.sub": "die Schutzfolie, mit der die Aufkleber überzogen werden",
    "cf.lam.loading": "Laminate werden geladen...",
    "cf.lam.error": "Laminate konnten nicht geladen werden.",
    "cf.retry": "Erneut versuchen",

    "cf.drop.here": "Dateien hier ablegen...",
    "cf.drop.hint": "Ziehen und ablegen oder klicken zum Auswählen",
    "cf.files.suffix": "Datei(en)",

    "cf.up.title": "Dateien werden hochgeladen...",
    "cf.up.wait": "Sie laden eine große Datei hoch — bitte haben Sie einen Moment Geduld.",
    "cf.up.remaining": "verbleibend",
    "cf.up.slowTitle": "Langsame Internetverbindung",
    "cf.up.slowDesc": "Der Upload dauert länger als gewöhnlich. Wechseln Sie zu einem schnelleren WLAN oder gehen Sie näher an den Router.",
    "cf.up.dontClose": "Schließen Sie die Seite während des Uploads nicht",
    "cf.up.cancel": "Upload abbrechen",

    "cf.file.uploaded": "Datei auf den Server hochgeladen",
    "cf.file.noThumb": "Bei einigen Dateiformaten (z. B. .tif, .ai, .eps) sind Vorschaubilder möglicherweise nicht sichtbar, die Dateien wurden jedoch erfolgreich hochgeladen.",

    "cf.three.desc": "Pro Bestellung können bis zu drei Designs mit demselben Material und Laminat angehängt werden. Alle Designs müssen ähnliche Maße haben.",
    "cf.three.notApply": "✨ Für Sie gilt das nicht — Sie haben individuelle Konditionen!",
    "cf.three.exampleTitle": "📌 Beispiel:",
    "cf.three.example1": "Weißes Material + mattes Laminat, Aufkleber 10×10 cm, 120 Stück — Sie können bis zu 3 verschiedene Designs hinzufügen.",
    "cf.three.example2": "Die Menge wird dann gleichmäßig auf die Designs aufgeteilt.",

    "cf.fmt.accepted": "Akzeptierte Formate:",
    "cf.fmt.max": "max.",
    "cf.fmt.perFile": "pro Datei",
    "cf.fmt.dpi": "Empfohlene Auflösung mind. 600 DPI (akzeptiert ab 300 DPI).",
    "cf.fmt.big1": "Dateien über 20 MB senden Sie bitte direkt an",
    "cf.fmt.big2": "oder über",
    "cf.fmt.contact": "Falls es ein Problem mit Ihren Dateien gibt — wir melden uns vor dem Druck bei Ihnen.",

    "cf.net": "netto",
    "cf.btn.added": "Hinzugefügt ✓",
    "cf.btn.uploading": "Dateien werden hochgeladen...",
    "cf.btn.share": "Einstellungen teilen",
    "cf.btn.shareHint": "Erstellt einen aktiven Link mit den gespeicherten Aufkleber-Einstellungen",
    "cf.btn.save": "Konfiguration speichern",
    "cf.cfg.loginSave": "Melden Sie sich an, um die Konfiguration zu speichern",
    "cf.cfg.namePrompt": "Namen für die Konfiguration vergeben (optional):",
    "cf.cfg.default": "Meine Konfiguration",
    "cf.cfg.saveError": "Fehler beim Speichern der Konfiguration",
    "cf.cfg.saved": "Konfiguration gespeichert! Sie finden sie in Ihrem Konto.",
  },

  it: {
    "cf.err.dims": "Inserisci dimensioni valide per l'adesivo",

    "cf.toast.fromContour": 'File "{f}" aggiunto dal creatore di contorni!',
    "cf.toast.fileAddedCart": 'File "{f}" aggiunto al carrello!',
    "cf.toast.productAdded": "Prodotto aggiunto al carrello!",
    "cf.toast.linkCopied": "Link copiato negli appunti!",
    "cf.toast.loginCart": "Accedi per aggiungere al carrello",
    "cf.toast.addedToCart": "Aggiunto al carrello!",
    "cf.toast.uploadCancelled": "Caricamento annullato",
    "cf.toast.compressed": "Compresse {n} immagini — risparmiati {mb} MB",

    "cf.foil.title": "Tipo di materiale",
    "cf.foil.sub": "è il supporto su cui vengono stampati gli adesivi",
    "cf.foil.loading": "Caricamento materiali...",
    "cf.foil.error": "Impossibile caricare i materiali.",
    "cf.foil.none": "Nessun materiale disponibile",
    "cf.lam.title": "Tipo di laminato",
    "cf.lam.sub": "è la pellicola che protegge gli adesivi",
    "cf.lam.loading": "Caricamento laminati...",
    "cf.lam.error": "Impossibile caricare i laminati.",
    "cf.retry": "Riprova",

    "cf.drop.here": "Trascina qui i file...",
    "cf.drop.hint": "Trascina e rilascia oppure clicca per scegliere",
    "cf.files.suffix": "file",

    "cf.up.title": "Caricamento dei file in corso...",
    "cf.up.wait": "Stai caricando un file di grandi dimensioni — un attimo di pazienza, può richiedere un po' di tempo.",
    "cf.up.remaining": "rimanenti",
    "cf.up.slowTitle": "Connessione internet lenta",
    "cf.up.slowDesc": "Il caricamento è più lento del solito. Prova a passare a una rete Wi-Fi più veloce o avvicinati al router.",
    "cf.up.dontClose": "Non chiudere la pagina durante il caricamento",
    "cf.up.cancel": "Annulla caricamento",

    "cf.file.uploaded": "File caricato sul server",
    "cf.file.noThumb": "Per alcuni formati di file (es. .tif, .ai, .eps) le miniature potrebbero non essere visibili, ma i file sono stati caricati con successo.",

    "cf.three.desc": "In un ordine puoi allegare fino a tre progetti realizzati con lo stesso materiale e laminato. Tutti i progetti devono avere dimensioni simili.",
    "cf.three.notApply": "✨ Questo non ti riguarda — hai condizioni personalizzate!",
    "cf.three.exampleTitle": "📌 Esempio:",
    "cf.three.example1": "Materiale bianco + laminato opaco, adesivi 10×10 cm, 120 pezzi — puoi aggiungere fino a 3 progetti diversi.",
    "cf.three.example2": "In questo caso la quantità viene divisa equamente tra i progetti.",

    "cf.fmt.accepted": "Formati accettati:",
    "cf.fmt.max": "max",
    "cf.fmt.perFile": "per file",
    "cf.fmt.dpi": "Risoluzione consigliata min. 600 DPI (accettata da 300 DPI).",
    "cf.fmt.big1": "I file superiori a 20 MB vanno inviati direttamente a",
    "cf.fmt.big2": "oppure tramite",
    "cf.fmt.contact": "Se ci sarà un problema con i file — ti contatteremo prima della stampa.",

    "cf.net": "netto",
    "cf.btn.added": "Aggiunto al carrello ✓",
    "cf.btn.uploading": "Caricamento dei file in corso...",
    "cf.btn.share": "Condividi impostazioni",
    "cf.btn.shareHint": "Crea un link attivo con le impostazioni salvate degli adesivi",
    "cf.btn.save": "Salva configurazione",
    "cf.cfg.loginSave": "Accedi per salvare la configurazione",
    "cf.cfg.namePrompt": "Assegna un nome alla configurazione (facoltativo):",
    "cf.cfg.default": "La mia configurazione",
    "cf.cfg.saveError": "Errore durante il salvataggio della configurazione",
    "cf.cfg.saved": "Configurazione salvata! La trovi nel tuo account.",
  },

  fr: {
    "cf.err.dims": "Indiquez des dimensions valides pour l'autocollant",

    "cf.toast.fromContour": 'Fichier "{f}" ajouté depuis le créateur de contours !',
    "cf.toast.fileAddedCart": 'Fichier "{f}" ajouté au panier !',
    "cf.toast.productAdded": "Produit ajouté au panier !",
    "cf.toast.linkCopied": "Lien copié dans le presse-papiers !",
    "cf.toast.loginCart": "Connectez-vous pour ajouter au panier",
    "cf.toast.addedToCart": "Ajouté au panier !",
    "cf.toast.uploadCancelled": "Téléversement annulé",
    "cf.toast.compressed": "{n} image(s) compressée(s) — {mb} MB économisés",

    "cf.foil.title": "Type de support",
    "cf.foil.sub": "c'est le support sur lequel les autocollants sont imprimés",
    "cf.foil.loading": "Chargement des supports...",
    "cf.foil.error": "Impossible de charger les supports.",
    "cf.foil.none": "Aucun support disponible",
    "cf.lam.title": "Type de pellicule de lamination",
    "cf.lam.sub": "c'est le film qui recouvre les autocollants",
    "cf.lam.loading": "Chargement des pellicules...",
    "cf.lam.error": "Impossible de charger les pellicules.",
    "cf.retry": "Réessayer",

    "cf.drop.here": "Déposez les fichiers ici...",
    "cf.drop.hint": "Glissez-déposez ou cliquez pour choisir",
    "cf.files.suffix": "fichier(s)",

    "cf.up.title": "Téléversement des fichiers...",
    "cf.up.wait": "Vous téléversez un fichier volumineux — merci de patienter, cela peut prendre un moment.",
    "cf.up.remaining": "restant",
    "cf.up.slowTitle": "Connexion internet lente",
    "cf.up.slowDesc": "Le téléversement est plus lent que d'habitude. Essayez un réseau Wi-Fi plus rapide ou rapprochez-vous du routeur.",
    "cf.up.dontClose": "Ne fermez pas la page pendant le téléversement",
    "cf.up.cancel": "Annuler le téléversement",

    "cf.file.uploaded": "Fichier téléversé sur le serveur",
    "cf.file.noThumb": "Pour certains formats de fichiers (ex. .tif, .ai, .eps), les miniatures peuvent ne pas s'afficher, mais les fichiers ont bien été téléversés.",

    "cf.three.desc": "Une commande peut inclure jusqu'à trois projets réalisés avec le même support et la même pellicule. Tous les projets doivent avoir des dimensions similaires.",
    "cf.three.notApply": "✨ Cela ne vous concerne pas — vous avez des conditions personnalisées !",
    "cf.three.exampleTitle": "📌 Exemple :",
    "cf.three.example1": "Support blanc + pellicule mate, autocollants 10×10 cm, 120 pièces — vous pouvez ajouter jusqu'à 3 projets différents.",
    "cf.three.example2": "Dans ce cas, la quantité est répartie équitablement entre les projets.",

    "cf.fmt.accepted": "Formats acceptés :",
    "cf.fmt.max": "max",
    "cf.fmt.perFile": "par fichier",
    "cf.fmt.dpi": "Résolution recommandée min. 600 DPI (acceptée à partir de 300 DPI).",
    "cf.fmt.big1": "Les fichiers de plus de 20 MB sont à envoyer directement à",
    "cf.fmt.big2": "ou via",
    "cf.fmt.contact": "En cas de problème avec vos fichiers — nous vous contacterons avant l'impression.",

    "cf.net": "HT",
    "cf.btn.added": "Ajouté au panier ✓",
    "cf.btn.uploading": "Téléversement des fichiers...",
    "cf.btn.share": "Partager les réglages",
    "cf.btn.shareHint": "Crée un lien actif avec les réglages enregistrés des autocollants",
    "cf.btn.save": "Enregistrer la configuration",
    "cf.cfg.loginSave": "Connectez-vous pour enregistrer la configuration",
    "cf.cfg.namePrompt": "Donnez un nom à la configuration (facultatif) :",
    "cf.cfg.default": "Ma configuration",
    "cf.cfg.saveError": "Erreur lors de l'enregistrement de la configuration",
    "cf.cfg.saved": "Configuration enregistrée ! Vous la trouverez dans votre compte.",
  },
};
